/*
 * Copyright (c) 2025 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
'use client';!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:mta-learning":true});}catch(e){}}();

import { PaymentCurrencies, StoryblokMtaMasterclass } from '@mta/vela-api-types'
import { MapPin } from 'lucide-react'
import React, { useMemo } from 'react'
import { MasterclassAddToBasket } from '@/components/e-commerce/masterclasses/MasterclassAddToBasket'
import { useMasterclassPrice } from '@/hooks/masterclasses/useMasterclassPrice'
import { getDateRange, getMonthAbbr } from '@/utils/date-formatting'
import { formatPrice } from '@/utils/price-formatting'

/** A description of the available properties for this component. */
type MasterclassCardProps = {
  /** The masterclass to show in the card. */
  masterclass: StoryblokMtaMasterclass
}

/**
 * Masterclass card component where the user can see crucial information for masterclasses, as well as controls to add
 * a masterclass to their basket. If the masterclass is historical (i.e. has already taken place), then the add to
 * basket functionality is disabled.
 */
const MasterclassCard: React.FC<MasterclassCardProps> = ({ masterclass }) => {
  const { name, location, start_date, end_date, note } = masterclass
  const startDate = useMemo(() => new Date(start_date), [start_date])
  const endDate = useMemo(() => new Date(end_date), [end_date])
  const historicalListing = useMemo(() => startDate < new Date(), [start_date])
  const pricing = useMasterclassPrice(masterclass)

  return (
    <div className='bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden grid grid-cols-1 lg:grid-cols-3'>
      <div
        className={`p-6 flex flex-col justify-center items-center text-center order-first bg-primary-500 text-white`}
      >
        <span className='text-lg uppercase font-semibold tracking-wider'>{getMonthAbbr(startDate)}</span>
        <span className='text-6xl font-extrabold leading-tight'>{getDateRange(startDate, endDate)}</span>
        <span className='text-lg font-medium'>{startDate.getFullYear()}</span>
      </div>
      <div className='lg:col-span-2 p-6 flex flex-col'>
        <h3 className='font-semibold text-2xl text-gray-900'>{name}</h3>
        <div className='flex flex-row items-center gap-1 text-md text-gray-600 mt-1'>
          <MapPin />
          {location}
        </div>
        {note && <p className='text-sm text-gray-600 italic mt-4'>{note}</p>}
        <div className='grow'></div>
        <div className='border-t border-gray-200 mt-4 pt-4 flex flex-col md:flex-row gap-4 items-center justify-between'>
          {pricing && (
            <>
              <p className='text-3xl font-bold text-gray-800'>
                {formatPrice(pricing.amount, PaymentCurrencies[pricing.currency])}
              </p>
              <MasterclassAddToBasket masterclass={masterclass} disabled={historicalListing} />
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export { MasterclassCard }
